import {
  Component, OnInit, OnDestroy, inject, signal, computed,
  ChangeDetectionStrategy, HostListener, isDevMode,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../../services/auth.service';
import { PermissionsService } from '../../../services/permissions.service';
import { ROUTE_OPTION_MAP } from '../../permissions/route-option-map';

interface OptionRow {
  optionId:   number;
  optionName: string;
  groupName:  string;
  actions:    { name: string; allow: boolean | null }[];
}

// Ordered to match legacy UI column order
const ACTION_NAMES = ['Leer', 'Escribir', 'Borrar', 'Autorizar', 'Festivos', 'Abrir', 'Enviar', 'Prescritos', 'Perfil', 'Ver Todo', 'Masivo', 'Salario', 'Foto', 'Agregar'];
const ACTION_IDS   = [1,      2,          3,        4,           5,          6,       7,        8,            9,        10,          11,       12,        13,     14];
const ACTION_ICONS: Record<number, string> = {
  1:  'pi-eye',
  2:  'pi-pencil',
  3:  'pi-trash',
  4:  'pi-check-circle',
  5:  'pi-sun',
  6:  'pi-folder-open',
  7:  'pi-send',
  8:  'pi-clipboard',
  9:  'pi-id-card',
  10: 'pi-eye',
  11: 'pi-file-edit',
  12: 'pi-dollar',
  13: 'pi-camera',
  14: 'pi-plus-circle',
};

@Component({
  selector: 'app-dev-permissions-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, FormsModule,
    DialogModule, Tabs, TabList, Tab, TabPanels, TabPanel,
    InputTextModule, IconFieldModule, InputIconModule, TooltipModule,
  ],
  templateUrl: './dev-permissions-panel.component.html',
  styleUrls: ['./dev-permissions-panel.component.scss'],
})
export class DevPermissionsPanelComponent implements OnInit, OnDestroy {
  private readonly authSvc  = inject(AuthService);
  protected readonly permSvc = inject(PermissionsService);

  visible    = signal(false);
  filterText = signal('');
  activeTab  = signal('permisos');

  readonly user       = computed(() => this.authSvc.decodeToken());
  readonly isSuperAdmin = computed(() => this.permSvc.isSuperAdmin());
  readonly isAllowed  = computed(() => isDevMode() || this.isSuperAdmin());
  readonly actionCols = ACTION_NAMES;
  readonly routeMap   = ROUTE_OPTION_MAP;

  readonly optionRows = computed<OptionRow[]>(() => {
    const rows = this.permSvc.rows();
    const byOption = new Map<number, OptionRow>();

    for (const r of rows) {
      if (!byOption.has(r.optionId)) {
        byOption.set(r.optionId, {
          optionId:   r.optionId,
          optionName: r.optionName,
          groupName:  r.groupName,
          actions:    ACTION_IDS.map(id => ({ name: '', allow: null })),
        });
      }
      const opt = byOption.get(r.optionId)!;
      const idx = ACTION_IDS.indexOf(r.actionId);
      if (idx >= 0) opt.actions[idx] = { name: r.actionName, allow: r.allow };
    }

    return Array.from(byOption.values()).sort((a, b) =>
      a.groupName.localeCompare(b.groupName) || a.optionName.localeCompare(b.optionName),
    );
  });

  readonly filteredRows = computed(() => {
    const q = this.filterText().toLowerCase().trim();
    if (!q) return this.optionRows();
    return this.optionRows().filter(r =>
      r.optionName.toLowerCase().includes(q) ||
      r.groupName.toLowerCase().includes(q)   ||
      String(r.optionId).includes(q),
    );
  });

  readonly routeRows = computed(() => {
    const q = this.filterText().toLowerCase().trim();
    return ROUTE_OPTION_MAP
      .filter(r => !q || r.route.includes(q) || r.label.toLowerCase().includes(q))
      .map(r => ({
        ...r,
        canRead: this.permSvc.canRead(r.optionId),
      }));
  });

  readonly allowedRoutes = computed(() =>
    this.routeRows().filter(r => r.canRead).length,
  );

  isMac():    boolean { return navigator.platform.toUpperCase().includes('MAC'); }
  isDevEnv(): boolean { return isDevMode(); }

  actionIcon(actionId: number): string { return ACTION_ICONS[actionId] ?? 'pi-circle'; }

  @HostListener('window:keydown', ['$event'])
  onKeydown(evt: KeyboardEvent): void {
    if (!this.isAllowed()) return;
    const isMac = navigator.platform.toUpperCase().includes('MAC');
    const trigger = isMac
      ? evt.metaKey && evt.shiftKey && evt.key.toLowerCase() === 'p'
      : evt.ctrlKey && evt.shiftKey && evt.key.toLowerCase() === 'p';

    if (trigger) {
      evt.preventDefault();
      this.toggle();
    }
  }

  toggle(): void {
    this.visible.update(v => !v);
    if (this.visible()) {
      this.filterText.set('');
      if (!this.permSvc.isLoaded()) {
        this.permSvc.loadPermissions().subscribe();
      }
    }
  }

  close(): void { this.visible.set(false); }

  ngOnInit(): void {}
  ngOnDestroy(): void {}
}
